import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import {
  LayoutObjectDto,
  UpdateLayoutObjectLayoutPositionRequest,
  UpdateLayoutObjectRequest,
} from '@yard/app/api'
import { EventTypes } from '@yard/messages/eventsTypes'
import { DeleteResponse } from '@yard/messages/models/delete-response.model'
import { CreateLayoutObjectRequest } from '@yard/pages/YardSetup/helpers/layout-object.mapper'
import { layoutObjectService } from '@yard/services/layoutObject.service'
import { computed, makeObservable } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export class LayoutObjectStore extends BaseEntityStore<LayoutObjectDto> {
  private isFetched = false
  private readonly disposers: Array<() => void> = []

  constructor(private readonly messageBus: MessageBus) {
    super()
    makeObservable(this, {
      layoutObjects: computed,
    })

    this.disposers.push(
      this.messageBus.subscribeEvent<LayoutObjectDto>(
        EventTypes.LayoutObjectUpsertedEvent,
        this.handleUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.LayoutObjectDeletedEvent,
        this.handleDeletedEvent,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
  }

  async loadLayoutObjects() {
    if (this.isFetched) return
    try {
      const layoutObjects = await layoutObjectService.getLayoutObjects()
      this.updateStoreItems(layoutObjects)
      this.isFetched = true
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async createLayoutObject(createCommand: CreateLayoutObjectRequest) {
    await layoutObjectService.create(createCommand)
  }

  async updateLayoutObject(updateCommand: UpdateLayoutObjectRequest) {
    await layoutObjectService.update(updateCommand)
  }

  async updateLayoutPosition(updateLayoutPositionCommand: UpdateLayoutObjectLayoutPositionRequest) {
    await appStore.triggerRequestWithoutLoader(() =>
      layoutObjectService.updateLayoutPosition(updateLayoutPositionCommand),
    )
  }

  async deleteLayoutObject(id: string) {
    await layoutObjectService.delete(id)
  }

  get layoutObjects(): Record<string, LayoutObjectDto> {
    return this.items.reduce(
      (acc, object) => {
        acc[object.id] = object
        return acc
      },
      {} as Record<string, LayoutObjectDto>,
    )
  }

  private handleUpsertedEvent = (event: IEvent<LayoutObjectDto>) => {
    const response = event.payload
    if (!response) return

    this.updateStoreItem(response, response.id)
  }

  private handleDeletedEvent = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    this.deleteStoreItem(response.id)
  }
}
